import {
  AccountType,
  ConsistencyRules,
  ConsistentRulesConfig,
  Program,
} from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';
import ProgramService from '@hub/services/entities/program-service';
import { EntityStore } from '../base/entity-store';

export class ProgramStore extends EntityStore<ProgramService, Program> {
  constructor() {
    const {
      apiFactory: { programService },
    } = getEnv();

    super(programService);
  }

  setPrograms(programs: Program[]) {
    this._items = new Map(programs.map((program) => [program._id, program]));
  }

  getNextPrograms(programId: string): Program[] {
    const programs: Program[] = [];

    let currentProgramId: string | undefined = programId;

    const program = this.get(currentProgramId);

    currentProgramId =
      program?.nextProgramId || program?.config.programEligibility;

    while (currentProgramId) {
      const program = this.get(currentProgramId);

      if (!program) {
        break;
      }

      programs.push(program);

      // get only the first funded program
      if (program.accountType === AccountType.Funded) {
        break;
      }

      currentProgramId =
        program.nextProgramId || program.config.programEligibility;
    }

    return programs;
  }

  hasDailyMetrics(programId: string): boolean {
    const program = this.get(programId);

    if (!program) {
      return false;
    }

    const dailyLimit =
      program.config.dailyPausePercentage ||
      program.config?.dailyLossPercentage ||
      program.config?.dailyPause ||
      0;
    return dailyLimit > 0;
  }

  hasViolations(_programId: string): boolean {
    return false;
  }

  hasRule(programId: string, rule: ConsistencyRules): boolean {
    const program = this.get(programId);

    if (!program) {
      return false;
    }

    const consistencyRules = program.config.consistencyRules || [];

    return consistencyRules.some(
      (consistencyRule) => consistencyRule.rule === rule,
    );
  }

  hasProfitableDays(programId: string): boolean {
    return this.hasRule(programId, ConsistencyRules.ProfitableDays);
  }

  hasInactiveDays(programId: string): boolean {
    const program = this.get(programId);

    return Boolean(program && program.config.inactivityDays);
  }

  getRule(
    programId: string,
    rule: ConsistencyRules,
  ): ConsistentRulesConfig | undefined {
    const program = this.get(programId);

    if (!program) {
      return;
    }

    return program.config.consistencyRules?.find(
      (consistencyRule) => consistencyRule.rule === rule,
    );
  }

  hasRules(programId: string): boolean {
    const program = this.get(programId);

    return Boolean(program?.config?.consistencyRules?.length);
  }
}
